import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";

/**
 * Staff websocket for live chat events.
 *
 *   ws(s)://<host>/ws/communication/?token=<knox token>
 *
 * Server -> client: `{type: "chat.message" | "chat.session" | "chat.typing", ref, ...}`.
 * Client -> server: `{type: "typing"}` (messages themselves are sent over REST).
 *
 * Safe to mount anywhere: it does nothing without a token, reconnects with
 * exponential backoff, and closes on unmount / logout. The latest `onEvent`
 * is read through a ref so callers need not memoise it.
 */

const trimSlash = (value) => String(value || "").replace(/\/+$/, "");

export const wsBase = () => {
  if (process.env.REACT_APP_WS_URL) return trimSlash(process.env.REACT_APP_WS_URL);
  const api = process.env.REACT_APP_BASE_URL;
  // nginx serves sockets at <host>/ws/ regardless of the API's own path
  // prefix (/api), so only the API's origin is reused.
  if (api && /^https?:\/\//i.test(api.trim())) {
    try {
      const url = new URL(api.trim());
      return `${url.protocol === "https:" ? "wss" : "ws"}://${url.host}/ws`;
    } catch (error) {
      /* fall through to same-origin */
    }
  }
  // Relative / unset API base: same origin as the app.
  if (typeof window !== "undefined") {
    const scheme = window.location.protocol === "https:" ? "wss" : "ws";
    return `${scheme}://${window.location.host}/ws`;
  }
  return "";
};

const MIN_DELAY = 1000;
const PING_MS = 25000; // keeps idle proxies (nginx proxy_read_timeout) from dropping us
// chatbot/consumers.py: CLOSE_UNAUTHORIZED / CLOSE_FORBIDDEN
const AUTH_CLOSE_CODES = [4401, 4403];
const MAX_DELAY = 30000;

const useCommunicationSocket = (onEvent, { enabled = true } = {}) => {
  const token = useSelector((state) => state.auth?.token);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !token || typeof WebSocket === "undefined") {
      setConnected(false);
      return undefined;
    }
    const base = wsBase();
    if (!base) return undefined;

    let stopped = false;
    let attempt = 0;
    let timer = null;
    let pinger = null;

    const connect = () => {
      if (stopped) return;
      let socket;
      try {
        socket = new WebSocket(
          `${base}/communication/?token=${encodeURIComponent(token)}`
        );
      } catch (error) {
        schedule();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        attempt = 0;
        setConnected(true);
        clearInterval(pinger);
        pinger = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_MS);
      };
      socket.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (error) {
          return;
        }
        if (!data || data.type === "pong") return;
        if (handlerRef.current) handlerRef.current(data);
      };
      socket.onerror = () => {
        /* onclose follows and handles the retry */
      };
      socket.onclose = (event) => {
        setConnected(false);
        clearInterval(pinger);
        if (socketRef.current === socket) socketRef.current = null;
        // The server rejected the token; retrying will not help until the
        // token changes (which re-runs this effect). Polling covers meanwhile.
        if (AUTH_CLOSE_CODES.includes(event?.code)) return;
        schedule();
      };
    };

    function schedule() {
      if (stopped) return;
      const delay = Math.min(MAX_DELAY, MIN_DELAY * 2 ** attempt);
      attempt += 1;
      // Jitter so a fleet of tabs does not reconnect in lockstep.
      timer = setTimeout(connect, delay + Math.random() * 500);
    }

    connect();

    return () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(pinger);
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        socket.onclose = null;
        try {
          socket.close();
        } catch (error) {
          /* already closed */
        }
      }
      setConnected(false);
    };
  }, [token, enabled]);

  const send = useCallback((payload) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  return { connected, send };
};

export default useCommunicationSocket;
